import { encodeAbiParameters, parseAbiParameters, getAddress } from 'viem'
import { multicallRetryUniversal } from '@1delta/providers'
import {
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

/** `uint256 bidId, address lender(20B), uint8 defaulted, 2 x uint256 owed`. */
const ROW_BYTES = 117

export const usesTellerLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.TELLER) !== undefined

/**
 * Teller discovery in ONE call instead of two sequential round trips.
 *
 * This family is the strongest case for folding discovery into the contract:
 * the builder costs ~1,742 ms across six chains and issues ZERO batch calls — it
 * is entirely pre-flight. `getBorrowerActiveLoanIds` must return before
 * `getLoanLender` can be asked per bid, so the two phases cannot even be
 * parallelised. Both are on-chain, so the lens answers them together.
 *
 * Returns `undefined` when the lens is unavailable or its answer is unusable, and
 * the caller falls back to the two-phase walk. It must NOT be read as "no loans":
 * an empty discovery and a failed one are the same shape here, and the difference
 * is the user's whole borrow position.
 */
export async function discoverTellerBidsViaLens(
  chainId: string,
  tellerV2: string,
  account: string,
): Promise<{ bidId: string; lender: string }[] | undefined> {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.TELLER)
  if (!lens || !tellerV2) return undefined

  // 0 = quote the obligation at `block.timestamp`. The owed figures the lens also
  // returns are NOT used here: the data phase reads them itself, so discovery
  // stays the only thing this call is trusted for.
  const payload = encodeAbiParameters(parseAbiParameters('address, uint256'), [
    tellerV2 as `0x${string}`,
    0n,
  ])

  let blob: string
  try {
    const res = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        {
          address: lens,
          name: 'getUserData',
          params: [[{ family: LENS_FAMILY.TELLER, payload }], account],
        },
      ],
      abi: PositionLensAbi as any,
      allowFailure: true,
    })) as any[]
    const blobs = res?.[0]
    if (!Array.isArray(blobs) || blobs.length !== 1) return undefined
    blob = blobs[0] as string
  } catch {
    return undefined
  }

  if (typeof blob !== 'string' || !blob.startsWith('0x') || blob.length < 6) {
    return undefined
  }
  const buf = Buffer.from(blob.slice(2), 'hex')
  const count = buf.readUInt16BE(0)
  // A stride disagreement must fail here, not surface as a lender address read out
  // of the middle of an amount.
  if (buf.length !== 2 + count * ROW_BYTES) return undefined

  const out: { bidId: string; lender: string }[] = []
  for (let i = 0; i < count; i++) {
    const o = 2 + i * ROW_BYTES
    out.push({
      bidId: BigInt('0x' + buf.subarray(o, o + 32).toString('hex')).toString(),
      lender: getAddress('0x' + buf.subarray(o + 32, o + 52).toString('hex')),
    })
  }
  return out
}
